import { useParams } from 'react-router';
import { useSearchParams } from 'react-router-dom';
import { useMemo, useState, useEffect, useCallback } from 'react';

import { Box } from '@mui/system';
import { Tab, Card, Grid, Tabs, alpha, Stack, CardHeader, Typography, CardContent } from '@mui/material';

import { parseHighlightTerms } from 'src/utils/highlight-terms';

import { useTranslate } from 'src/locales';
import { rootApi } from 'src/services/rootApi';
import { useAppDispatch } from 'src/store/store';
import { DocumentType } from 'src/services/files/file.types';
import { ListEntityType } from 'src/services/lists/lists.types';
import { SigningOriginType } from 'src/services/signing/signing.types';
import { OrganizationCandidateType } from 'src/services/candidates/candidates.types';
import { useGetCandidateDocumentsQuery, useGetOrganizationCandidateQuery, useGetOrgCandidateActivitiesQuery, useGetOrganizationCandidateSyncedProfileQuery } from 'src/services/candidates/candidates.service';

import CandidateResumeView from 'src/components/candidates/candidate-resume-view';

import ActivityFeed from 'src/sections/activity/card/activity-feed';
import CandidateJobsList from 'src/sections/candidates/jobs/jobs-table-view';
import ProfileUser from 'src/sections/profile/profile-sections/profile-user';
import SigningDocumentsList from 'src/sections/signing/signing-documents-list';
import { EntityListsCard } from 'src/sections/lists/components/entity-lists-card';
import CandidateProfileEditor from 'src/sections/profile/profile-sections/profile-editor';
import { useCandidateActions } from 'src/sections/candidates/hooks/use-candidate-actions';
import { CandidateDetailSection } from 'src/sections/candidates/detail/sections/detail-section';
import CandidateDocumentsView from 'src/sections/candidates/detail/sections/candidate-documents';
import { LinkedContactsCard } from 'src/sections/candidates/detail/sections/linked-contacts-card';
import { CandidateLabelsCard } from 'src/sections/candidates/detail/sections/candidate-labels-card';
import { CandidateContactCard } from 'src/sections/candidates/detail/sections/candidate-contact-card';

// ----------------------------------------------------------------------

enum TabValue {
  PROFILE = 'profile',
  RESUME = 'resume',
  JOBS = 'jobs',
  DETAIL = 'detail',
  DOCUMENTS = 'documents',
}

enum ProfileTabValue {
  CRM_PROFILE = 'crm_profile',
  LIVE_PROFILE = 'live_profile'
}

interface Props {
  overrideCandidateId?: string;
  /** Preselects this job in Add to job — set when the profile is opened from a job's Sourcing tab. */
  jobId?: string;
}

export default function CandidateDetail({ overrideCandidateId, jobId }: Props) {

  const [selectedTab, onSelectTab] = useState<TabValue>();
  const [selectedProfileTab, onSelectProfileTab] = useState<ProfileTabValue>(ProfileTabValue.CRM_PROFILE);

  const { t } = useTranslate();

  const { candidate_id: candidateIdParam } = useParams();

  const dispatch = useAppDispatch();

  const [searchParams, setSearchParams] = useSearchParams();

  const candidateId = useMemo(() => {
    if (overrideCandidateId) return overrideCandidateId;

    return candidateIdParam;
  }, [overrideCandidateId, candidateIdParam]);

  const [activitiesPage, setActivitiesPage] = useState({ page: 1, per_page: 10 });

  const { currentData: orgCandidate, isFetching: isLoading } = useGetOrganizationCandidateQuery(
    candidateId as string,
    {
      skip: !candidateId,
    }
  );

  // The live profile is fetched only once its tab is opened (JT1-1605). It used to arrive inline on
  // every candidate open, which meant the detail endpoint built a second full profile graph that most
  // opens never rendered. `orgCandidate.synced_profile` still carries id, location and title for the
  // record header and contact card.
  const { currentData: syncedProfile, isFetching: isSyncedProfileLoading } =
    useGetOrganizationCandidateSyncedProfileQuery(candidateId as string, {
      skip: !candidateId || selectedProfileTab !== ProfileTabValue.LIVE_PROFILE,
    });

  const { data: activities } = useGetOrgCandidateActivitiesQuery({
    candidate_id: candidateId as string,
    page: activitiesPage.page,
    perPage: activitiesPage.per_page,
  }, { skip: !candidateId });

  // Resume tab (spec item 18, JT1-1537): the newest RESUME document (for the PDF fallback) plus the
  // parsed resume_text on the org candidate. The tab appears only when at least one of them exists.
  const { data: resumeDocs } = useGetCandidateDocumentsQuery(
    { candidate_id: candidateId as string, params: { page: 1, per_page: 1, type: DocumentType.RESUME } },
    { skip: !candidateId }
  );
  const resumeDoc = resumeDocs?.results?.[0];
  const hasResume = Boolean(orgCandidate?.resume_text) || Boolean(resumeDoc);

  // Highlight resume search terms when the profile was opened from a search (?q=…).
  const highlightTerms = useMemo(() => parseHighlightTerms(searchParams.get('q')), [searchParams]);

  // Every candidate action, shared with the quick view, the list toolbar and the application profile
  // (JT1-1799).
  const { actions: candidateActions, modals: candidateActionModals } = useCandidateActions({
    mode: 'record',
    ids: candidateId ? [candidateId] : [],
    jobId,
  });

  useEffect(() => {
    // eslint-disable-next-line no-useless-return
    if (!selectedTab) return;

    // Embedded in the record overlay (JT1-1529): the tab is local state. Writing it to the URL would
    // replace the whole query string and wipe the co-owned ?preview params, closing the overlay — the
    // same clobber fixed in candidate-list-view. Only sync the tab on the standalone profile page.
    if (overrideCandidateId) return;

    // MERGE the tab into the existing query rather than replacing it, so co-owned params survive —
    // e.g. the ?q= search term the Resume tab highlights (JT1-1537). A bare object replace wiped it.
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set('tab', selectedTab);
        return next;
      },
      { replace: true }
    );

  }, [selectedTab, setSearchParams, overrideCandidateId]);

  useEffect(() => {
    // eslint-disable-next-line no-useless-return
    if (selectedTab) return;

    let defaultTab: string = TabValue.DETAIL;

    const existingTab = searchParams.get('tab');
    if (existingTab && Object.values(TabValue).includes(existingTab as TabValue)) {
      defaultTab = existingTab;
    }

    onSelectTab(defaultTab as TabValue);

  }, [selectedTab, searchParams]);

  const onProfileUpdate = useCallback(() => {
    dispatch(rootApi.util.invalidateTags([{ type: 'OrgCandidates', id: candidateId as string }]));

  }, [dispatch, candidateId]);

  return (
    <Grid container gap={3}>
      <Grid item xs={12}>
        <ProfileUser
          profile={orgCandidate?.candidate_profile}
          isLoading={isLoading}
          mode="view"
          fallbackLocation={orgCandidate?.synced_profile?.location}
          commsEntityId={candidateId as string}
          showAvailabilityStatus={orgCandidate?.type !== OrganizationCandidateType.UNLINKED}
          actions={candidateActions}
        />
      </Grid>

      <Grid item xs={12}>
        <Tabs
          value={selectedTab}
          onChange={(event, newValue) => onSelectTab(newValue)}
          sx={{
            px: 2.5,
            mb: { xs: 1, sm: 'unset' },
            boxShadow: (elTheme) => `inset 0 -2px 0 0 ${alpha(elTheme.palette.grey[500], 0.08)}`,
          }}
        >
          <Tab
            key={TabValue.DETAIL}
            iconPosition="end"
            value={TabValue.DETAIL}
            label={
              <Typography
                variant="subtitle2"
                sx={{ textTransform: 'capitalize', fontWeight: 'bold' }}
              >
                {t('candidates.detail.tabs.detail')}
              </Typography>
            }
          />

          <Tab
            key={TabValue.PROFILE}
            iconPosition="end"
            value={TabValue.PROFILE}
            label={
              <Typography
                variant="subtitle2"
                sx={{ textTransform: 'capitalize', fontWeight: 'bold' }}
              >
                {t('candidates.detail.tabs.profile')}
              </Typography>
            }
          />

          {hasResume && (
            <Tab
              key={TabValue.RESUME}
              iconPosition="end"
              value={TabValue.RESUME}
              label={
                <Typography
                  variant="subtitle2"
                  sx={{ textTransform: 'capitalize', fontWeight: 'bold' }}
                >
                  {t('candidates.detail.tabs.resume')}
                </Typography>
              }
            />
          )}

          <Tab
            key={TabValue.JOBS}
            iconPosition="end"
            value={TabValue.JOBS}
            label={
              <Typography
                variant="subtitle2"
                sx={{ textTransform: 'capitalize', fontWeight: 'bold' }}
              >
                {t('candidates.detail.tabs.jobs')}
              </Typography>
            }
          />

          <Tab
            key={TabValue.DOCUMENTS}
            iconPosition="end"
            value={TabValue.DOCUMENTS}
            label={
              <Typography
                variant="subtitle2"
                sx={{ textTransform: 'capitalize', fontWeight: 'bold' }}
              >
                {t('candidates.detail.tabs.documents')}
              </Typography>
            }
          />

        </Tabs>
      </Grid>


      <Grid container spacing={2}>
        <Grid item xs={12} md={8}>
          {selectedTab === TabValue.DETAIL && (
            <Stack spacing={2}>
              <CandidateDetailSection candidateId={candidateId as string} />
              <Card>
                <CardHeader title={t('admin-connections.detail.activities')} />
                <CardContent>
                  <ActivityFeed
                    activities={activities?.results ?? []}
                    page={activitiesPage.page}
                    perPage={activitiesPage.per_page}
                    onPageChange={(_e, newPage) => setActivitiesPage({ ...activitiesPage, page: newPage })}
                    totalItems={activities?.count ?? 0}
                  />
                </CardContent>
              </Card>
            </Stack>
          )}

          {selectedTab === 'profile' && (
            <>
              {
                orgCandidate?.type === OrganizationCandidateType.LINKED && (
                  <Box mb={2}>
                    <Tabs
                      value={selectedProfileTab}
                      onChange={(event, newValue) => onSelectProfileTab(newValue)}
                      sx={{
                        px: 2.5,
                        mb: { xs: 1, sm: 'unset' },
                        boxShadow: (elTheme) => `inset 0 -2px 0 0 ${alpha(elTheme.palette.grey[500], 0.08)}`,
                      }}
                    >
                      <Tab
                        key={ProfileTabValue.CRM_PROFILE}
                        iconPosition="end"
                        value={ProfileTabValue.CRM_PROFILE}
                        label={
                          <Typography
                            variant="subtitle2"
                            sx={{ textTransform: 'capitalize', fontWeight: 'bold' }}
                          >
                            {t('candidates.detail.tabs.crm_profile')}
                          </Typography>
                        }
                      />

                      <Tab
                        key={ProfileTabValue.LIVE_PROFILE}
                        iconPosition="end"
                        value={ProfileTabValue.LIVE_PROFILE}
                        label={
                          <Typography
                            variant="subtitle2"
                            sx={{ textTransform: 'capitalize', fontWeight: 'bold' }}
                          >
                            {t('candidates.detail.tabs.live_profile')}
                          </Typography>
                        }
                      />
                    </Tabs>
                  </Box>
                )
              }

              {
                selectedProfileTab === ProfileTabValue.CRM_PROFILE && (
                  <CandidateProfileEditor
                    onUpdate={onProfileUpdate}
                    profile={orgCandidate?.candidate_profile}
                    isLoading={isLoading}
                    mode="edit"
                    commsEntityId={candidateId as string}
                    highlightTerms={highlightTerms}
                  />
                )
              }

              {
                selectedProfileTab === ProfileTabValue.LIVE_PROFILE && (
                  <CandidateProfileEditor
                    onUpdate={() => { }}
                    profile={syncedProfile ?? undefined}
                    isLoading={isLoading || isSyncedProfileLoading}
                    mode="view"
                    commsEntityId={candidateId as string}
                    highlightTerms={highlightTerms}
                  />
                )
              }
            </>
          )}

          {selectedTab === TabValue.RESUME && (
            <Card>
              <CardContent>
                {/* Default to the supplied formatted PDF; CandidateResumeView falls back to parsed text
                    when there is no document, and still forces text when highlight terms exist (JT1-1537 QA). */}
                <CandidateResumeView
                  resumeText={orgCandidate?.resume_text}
                  resumeDocUrl={resumeDoc?.document?.public_path}
                  highlightTerms={highlightTerms}
                  prefer="document"
                />
              </CardContent>
            </Card>
          )}

          {selectedTab === 'jobs' && (
            <CandidateJobsList candidateId={candidateId as string} />
          )}

          {selectedTab === 'documents' && (
            <Stack spacing={3}>
              <CandidateDocumentsView candidateId={candidateId as string} />
              <SigningDocumentsList originType={SigningOriginType.CANDIDATE} originId={candidateId as string} />
            </Stack>
          )}
        </Grid>
        
        <Grid item xs={12} md={4}>
          <Stack direction="column" spacing={2}>
            {/* Candidate's contact information */}
            <CandidateContactCard candidateId={candidateId as string} />

            {/* Candidate's linked contacts */}
            {orgCandidate && (
              <>
                <LinkedContactsCard candidateId={orgCandidate.id} />
                <CandidateLabelsCard candidateId={orgCandidate.id} />
                {/* JT1-1276 Phase 5: lists this candidate belongs to (below Linked) */}
                <EntityListsCard
                  entityType={ListEntityType.CANDIDATE}
                  entityId={orgCandidate.id}
                />
              </>
            )}
          </Stack>
        </Grid>
      </Grid>

      {candidateActionModals}
    </Grid>
  );
}
